<template>
	<div v-if="isUserAllowedInLevel5or6">
		<BaseHeader :title="$t('administrate_vehicle_file').toString()" fontawesome-title="car">
			<BaseCheckbox
				v-show="vehicle.id"
				:label="$t('deactivate_vehicle')"
				fontawesome-before-label="car-crash"
				class="mr-8 mb-0 pt-3"
				@asked="toggleVehicleCheckbox"
				v-model="status"
				:ask-before-change="true"
				:ask-before-change-question="this.vehicle.deactivated_by_repairshop ? $t('vehicle_activate') : $t('vehicle_deactivate')"
			></BaseCheckbox>
		</BaseHeader>
		<div class="main">
			<div class="mb-8 border-b-2">
				<BaseCard :title="$t('processes').toString()" class="p-0">
					<CustomerProcessList :processes="processes" :customer="customer" v-if="processes && processes.length" />
					<div v-else-if="processes && processes.length === 0">
						{{ $t('vehicle_no_processes') }}
					</div>
				</BaseCard>
			</div>

			<LoadingSpinnerComponent v-if="!vehicle.id" />
			<div class="flex flex-wrap lg:flex-no-wrap justify-center space-x-8" v-show="vehicle.id">
				<div class="lg:w-1/3 md:flex md:space-x-8 lg:space-x-0 lg:block mb-4 lg:space-y-8">
					<div>
						<div class="flex justify-center" v-if="overviewImage">
							<BaseImage :src="overviewImage" :alt="$t('overview_image')" class="mb-4 h-auto" />
						</div>
						<BaseCamera
							orientation="landscape"
							@snapshot="updateOverviewImage"
							:label="btnLabel"
							:take-image-label="$t('activate_camera_for_overview_image')"
							:remove-upload-button="isAppContainer"
							:take-image="true"
							:allow-upload-in-app-container="false"
							:enable-preview="false"
						/>
					</div>
					<div>
						<div class="flex justify-center" v-if="vehicleRegistrationImage">
							<BaseImage :src="vehicleRegistrationImage" :alt="$t('vehicle_registration')" class="mb-4 h-auto" />
						</div>
						<BaseCamera
							orientation="landscape"
							@snapshot="updateRegistrationImage"
							:label="vehicleRegistrationImageBtnLabel"
							:take-image-label="$t('activate_camera_for_registration_image')"
							:remove-upload-button="isAppContainer"
							:take-image="true"
							:allow-upload-in-app-container="false"
							:enable-preview="false"
						/>
					</div>
					<div>
						<div class="flex justify-center" v-if="vinImage">
							<BaseImage :src="vinImage" :alt="$t('vehicle_registration')" class="mb-4 h-auto" />
						</div>
						<BaseCamera
							orientation="landscape"
							@snapshot="updateVinImage"
							:label="vinImageButtonLabel"
							:take-image-label="$t('activate_camera_for_vin_image')"
							:remove-upload-button="isAppContainer"
							:take-image="true"
							:allow-upload-in-app-container="false"
							:enable-preview="false"
						/>
					</div>
				</div>
				<div class="lg:w-2/3">
					<CustomerVehicleForm
						ref="CustomerVehicleForm"
						v-model="vehicle"
						:display="true"
						:is-customer-car="true"
						:is-customer-car-edit="true"
						@updateValue="buttonState = false"
					/>
				</div>
			</div>
		</div>
		<Footer @backButtonClick="goBack" :is-continue-button="false" :is-skippable="false" :is-back-button="true" class="m-8">
			<template #right>
				<BaseButton @click="save" :disabled="buttonState" class="mr-4 btn-blue">{{ $t('save_changes') }}</BaseButton>
			</template>
		</Footer>
	</div>
	<Forbidden v-else />
</template>

<script>
import { CustomersApiFactory, VehiclesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import CustomerVehicleForm from '@/components/forms/CustomerVehicleForm';
import BaseButton from '@/components/forms/buttons/BaseButton';
import Footer from '@/components/damagecases/Footer';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import BaseCamera from '@/components/forms/inputs/BaseCamera';
import CustomerProcessList from '@/views/management/Customer/CustomerProcessList';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import BaseImage from '@/components/forms/BaseImage';
import { default as LoadingSpinnerComponent } from '@/components/navigation/LoadingSpinner';
import VehicleRepository from '@/api/repositories/VehicleRepository';
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseCard from '@/components/damagecases/forms/BaseCard';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox.vue';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';

export default {
	name: 'VehiclesEdit',
	components: {
		Forbidden,
		BaseCheckbox,
		BaseCard,
		BaseHeader,
		LoadingSpinnerComponent,
		BaseImage,
		CustomerProcessList,
		BaseCamera,
		Footer,
		BaseButton,
		CustomerVehicleForm,
	},
	mixins: [AppContainerMixin, LoadingSpinner, Authentication],
	data() {
		return {
			changedVehicle: false,
			changedOverviewImage: false,
			changedVehicleRegistrationImage: false,
			changedVinImage: false,
			vehicle: {},
			overviewImage: null,
			vehicleRegistrationImage: null,
			vinImage: null,
			customer: {},
			processes: undefined,
			status: false,
			buttonState: true,
		};
	},
	mounted() {
		if (this.isUserAllowedInLevel5or6) {
			this.customer.id = this.$route.params.customer;
			this.loadVehicleData();
		}
	},
	computed: {
		btnLabel() {
			if (this.overviewImage) return this.$t('change_overview_image');
			return this.$t('store_overview_image');
		},
		vehicleRegistrationImageBtnLabel() {
			if (this.overviewImage) return this.$t('change_registration_image');
			return this.$t('store_registration_image');
		},
		vinImageButtonLabel() {
			if (this.vinImage) return this.$t('change_vin_photo');
			return this.$t('store_vin_photo');
		},
		showStep2() {
			return (this.overviewImage || this.skipOverviewImage) && !this.vehicleRegistrationImageContent;
		},
		showStep3() {
			return this.vehicleRegistrationImage || this.skipVehicleRegistrationImage;
		},
	},
	methods: {
		updateRegistrationImage(image) {
			this.vehicleRegistrationImage = image;
			this.changedVehicleRegistrationImage = true;
			this.buttonState = false;
			this.setLeaveMessage();
		},
		updateOverviewImage(image) {
			this.overviewImage = image;
			this.changedOverviewImage = true;
			this.buttonState = false;
			this.setLeaveMessage();
		},
		updateVinImage(image) {
			this.vinImage = image;
			this.changedVinImage = true;
			this.buttonState = false;
			this.setLeaveMessage();
		},
		setLeaveMessage() {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		},
		save() {
			if (this.$refs.CustomerVehicleForm.validate()) {
				this.setLoadingStatus(true);
				this.vehicle.customer = { id: this.$route.params.customer };
				this.vehicle.overviewImage = this.overviewImage;
				this.vehicle.vehicleRegistrationImage = this.vehicleRegistrationImageContent || {};
				this.vehicle.vehicleRegistrationImage.image = this.vehicleRegistrationImage; // TODO cleanup

				this.saveVehicle()
					.then(() => this.saveOverViewImage())
					.then(() => this.saveVehicleRegistrationImage())
					.then(() => this.saveVehicleVinImage())
					.then(response => {
						this.$swal({
							title: this.$t('general.success').toString(),
							text: this.$t('vehicle_saved').toString(),
							icon: 'success',
						});
						this.$store.commit('setConfirmLeaving', false);
						this.loadVehicleData();
						this.buttonState = true;
					})
					.catch(error => {
						this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
					})
					.finally(() => this.setLoadingStatus(false));
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		saveVehicleRegistrationImage() {
			if (!this.changedVehicleRegistrationImage) return;
			return new VehicleRepository().updateVehicleRegistrationImage(this.vehicle.id, this.vehicleRegistrationImage);
		},
		saveVehicleVinImage() {
			if (!this.changedVinImage) return;
			return new VehicleRepository().updateVehicleVinImage(this.vehicle.id, this.vinImage);
		},
		saveOverViewImage() {
			if (!this.changedOverviewImage) return;
			const vehicle = { customerId: this.$route.params.customer, overviewImage: this.overviewImage };
			return VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).vehiclesUpdateoverviewimageIdPut(
				this.$route.params.vehicleId,
				vehicle,
			);
		},
		saveVehicle() {
			return VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).vehiclesIdPut(
				this.$route.params.vehicleId,
				this.vehicle,
			);
		},
		goBack() {
			if (this.$route.params.backlink === undefined) {
				this.$router.push({ path: '/management/customer/overview/' + this.$route.params.customer });
			} else {
				this.$router.go(-1);
			}
		},
		toggleVehicleCheckbox() {
			VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.vehiclesIdChangeStatePut(this.vehicle.id)
				.then(response => {
					this.loadVehicleData();
				});
		},
		loadProcesses(vehicleId) {
			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customersIdProcessesGet(this.$route.params.customer, vehicleId, undefined)
				.then(response => {
					this.processes = response.data.data;
				});
		},
		loadVehicleData() {
			VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.vehiclesIdGet(this.$route.params.vehicleId)
				.then(response => {
					this.status = response.data.deactivated_by_repairshop != null;
					this.vehicle = response.data;
					this.vehicle.registrationFirst = response.data.registrationFirstRaw;
					this.vehicle.registrationLast = response.data.registrationLastRaw;
					if (this.vehicle.overviewImage) {
						this.overviewImage = this.vehicle.overviewImage.base64Content;
					}
					if (this.vehicle.vehicleRegistrationImage) {
						this.vehicleRegistrationImage = this.vehicle.vehicleRegistrationImage.base64Content;
					}
					if (this.vehicle.vinImage) {
						this.vinImage = this.vehicle.vinImage.base64Content;
					}

					this.vehicle.au = response.data.au;
					this.vehicle.hu = response.data.hu;
					this.loadProcesses(this.$route.params.vehicleId);
				});
		},
	},
};
</script>

<style scoped></style>
